import React from 'react';

/**
 * Service de détection de connexion robuste
 * Utilise plusieurs méthodes pour détecter l'état de la connexion
 */

interface ConnectionStatus {
  isOnline: boolean;
  lastChecked: Date;
  method: 'navigator' | 'ping' | 'timeout';
}

class ConnectionDetectorService {
  private status: ConnectionStatus = {
    isOnline: navigator.onLine,
    lastChecked: new Date(),
    method: 'navigator'
  };

  private listeners: Array<(status: ConnectionStatus) => void> = [];
  private pingInterval: NodeJS.Timeout | null = null;
  private readonly PING_INTERVAL = 10000; // 10 secondes
  private readonly PING_TIMEOUT = 3000; // 3 secondes

  constructor() {
    this.setupNavigatorListeners();
    
    // Vérification immédiate et plus fréquente au démarrage
    this.performInitialCheck();
    
    // Démarrer le ping périodique après la vérification initiale
    setTimeout(() => {
      this.startPeriodicPing();
    }, 2000); // Attendre 2 secondes avant de démarrer le ping périodique
  }

  private async performInitialCheck() {
    // Vérification immédiate
    try {
      const isOnline = await this.checkConnection();
      this.updateStatus(isOnline, 'ping');
    } catch (error) {
      this.updateStatus(false, 'ping');
    }
    
    // Vérifications supplémentaires rapides pour s'assurer de l'état
    setTimeout(async () => {
      try {
        const isOnline = await this.checkConnection();
        this.updateStatus(isOnline, 'ping');
      } catch (error) {
        this.updateStatus(false, 'ping');
      }
    }, 500);
    
    setTimeout(async () => {
      try {
        const isOnline = await this.checkConnection();
        this.updateStatus(isOnline, 'ping');
      } catch (error) {
        this.updateStatus(false, 'ping');
      }
    }, 1500);
  }

  private setupNavigatorListeners() {
    window.addEventListener('online', () => {
      this.updateStatus(true, 'navigator');
    });

    window.addEventListener('offline', () => {
      this.updateStatus(false, 'navigator');
    });
  }

  private async pingServer(): Promise<boolean> {
    try {
      // Test rapide: Internet d'abord (plus rapide à détecter)
      const internetController = new AbortController();
      const internetTimeoutId = setTimeout(() => internetController.abort(), this.PING_TIMEOUT);
      
      try {
        await fetch('https://www.google.com/favicon.ico', {
          method: 'HEAD',
          signal: internetController.signal,
          cache: 'no-store',
          mode: 'no-cors'
        });
        
        clearTimeout(internetTimeoutId);
        
        // Si internet fonctionne, tester rapidement le serveur local
        const localController = new AbortController();
        const localTimeoutId = setTimeout(() => localController.abort(), 2000); // Timeout plus court pour le serveur local
        
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(7);
        
        await fetch(`/weighing/api/pesees/?_t=${timestamp}&_r=${randomId}`, {
          method: 'HEAD',
          signal: localController.signal,
          cache: 'no-store'
        });
        
        clearTimeout(localTimeoutId);
        return true; // Internet + Serveur local OK
        
      } catch (internetError: any) {
        clearTimeout(internetTimeoutId);
        
        // Si internet ne fonctionne pas, tester quand même le serveur local
        const localController = new AbortController();
        const localTimeoutId = setTimeout(() => localController.abort(), this.PING_TIMEOUT);
        
        try {
          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substring(7);
          
          await fetch(`/weighing/api/pesees/?_t=${timestamp}&_r=${randomId}`, {
            method: 'HEAD',
            signal: localController.signal,
            cache: 'no-store'
          });
          
          clearTimeout(localTimeoutId);
          return false; // Serveur local OK mais pas internet = mode offline
          
        } catch (localError: any) {
          clearTimeout(localTimeoutId);
          return false; // Ni serveur local ni internet
        }
      }
      
    } catch (error: any) {
      // Si c'est une erreur de réseau (pas de connexion), on est offline
      if (error.name === 'AbortError' || 
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.message.includes('ERR_NETWORK') ||
          error.message.includes('ERR_INTERNET_DISCONNECTED') ||
          error.message.includes('ERR_CONNECTION_REFUSED')) {
        return false;
      }
      
      // Autres erreurs = on considère qu'on est connecté
      return true;
    }
  }

  private async checkConnection(): Promise<boolean> {
    // Toujours faire un ping réel pour vérifier la connexion
    // navigator.onLine n'est pas fiable
    try {
      return await this.pingServer();
    } catch (error) {
      return false;
    }
  }

  private async updateStatus(isOnline: boolean, method: ConnectionStatus['method']) {
    const previousStatus = this.status.isOnline;
    this.status = {
      isOnline,
      lastChecked: new Date(),
      method
    };

    // Notifier seulement si le statut a changé
    if (previousStatus !== isOnline) {
      
      this.notifyListeners();
    }
  }

  private startPeriodicPing() {
    this.pingInterval = setInterval(async () => {
      const isOnline = await this.checkConnection();
      this.updateStatus(isOnline, 'ping');
    }, this.PING_INTERVAL);
  }

  private notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.status);
      } catch (error) {
        console.error('Erreur notification listener:', error);
      }
    });
  }

  // Méthodes publiques
  public getStatus(): ConnectionStatus {
    return { ...this.status };
  }

  public async forceCheck(): Promise<boolean> {
    const isOnline = await this.checkConnection();
    this.updateStatus(isOnline, 'ping');
    return isOnline;
  }

  public addListener(listener: (status: ConnectionStatus) => void): () => void {
    this.listeners.push(listener);
    
    // Retourner une fonction pour supprimer le listener
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  public destroy() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    this.listeners = [];
  }
}

// Instance singleton
export const connectionDetector = new ConnectionDetectorService();

// Hook React pour utiliser le détecteur de connexion
export const useConnectionDetector = () => {
  const [status, setStatus] = React.useState(connectionDetector.getStatus());

  React.useEffect(() => {
    const removeListener = connectionDetector.addListener(setStatus);
    return removeListener;
  }, []);

  return {
    isOnline: status.isOnline,
    lastChecked: status.lastChecked,
    method: status.method,
    forceCheck: () => connectionDetector.forceCheck()
  };
};
